/**
 * Token-utility helpers.
 *
 * Two token shapes are supported:
 *
 *   1. Single-value DTCG (most tokens):
 *
 *        "primary": { "$type": "color", "$value": "#3e88c5" }
 *
 *   2. Dual-context DTCG (used for `surface` after Path-2 restructure):
 *
 *        "background": {
 *          "$type": "color",
 *          "$value": { "dark": "#141418", "light": "#fafbfe" }
 *        }
 *
 *      One token = one semantic role, with a value per context. Mirrors
 *      the Figma Dark and Light columns in the Semantics collection.
 *
 * `v(token)` always returns the DARK value. Single-string `$value` is
 * treated as dark-only for back-compat. `vLight(token)` returns the
 * LIGHT value and falls back to dark for legacy single-value tokens.
 *
 * Centralising the unwrap here prevents the per-file copies that used to
 * drift across Button, Input, Modal, SegmentedButton, LogoAssemble,
 * AppHeader, JobGroupNode, theme/index, typography, storybook/storyHelpers.
 *
 * @format
 */

type DualValue<T> = {dark: T; light: T};
type TokenValue<T> = T | DualValue<T>;

function isDual<T>(val: unknown): val is DualValue<T> {
  return (
    val !== null &&
    typeof val === 'object' &&
    'dark' in (val as object) &&
    'light' in (val as object)
  );
}

export const v = <T>(t: {$value: TokenValue<T>}): T => {
  const val = t.$value;
  return isDual<T>(val) ? val.dark : (val as T);
};

export const vLight = <T>(t: {$value: TokenValue<T>}): T => {
  const val = t.$value;
  return isDual<T>(val) ? val.light : (val as T);
};
